import { useEffect, useMemo, useState } from "react";
import {
  CopyOutlined,
  DeleteOutlined,
  MenuOutlined,
  PlusOutlined,
  SaveOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Segmented,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Select,
  Space,
  Switch,
  Tabs,
  Typography,
} from "antd";

import {
  createSignupFlowNode,
  createStarterSignupFlowGraph,
  LandingPageKind,
  landingPageConfigSchema,
  LandingSignupFlowActionType,
  SIGNUP_FLOW_NODE_TYPES,
  signupFlowNodeTypeLabel,
  type LandingPage,
  type LandingPageConfig,
  type LandingQuestionOption,
  type LandingSectionConfig,
  type LandingSectionType,
  type LandingSignupFlowGraph,
  type SignupFlowEdge,
  type SignupFlowNode,
  type SignupFlowNodeType,
} from "@fitness-app/data-models/entities/LandingPage";

import { funnelSectionDefinitions } from "@fitness-app/data-models/landing-templates/index";

import { getDefaultSectionProps, landingSectionTypeOptions, pakietyStaticSectionOptions } from "./defaultLandingConfig";
import {
  FUNNEL_TEMPLATE_SLUG_PREFIX,
  LANDING_SLUG_PREFIX,
  normalizeFunnelPageSlug,
  normalizeLandingSlug,
} from "./landingSlug";
import { SchemaPropsEditor } from "./SchemaPropsEditor";

interface LandingEditorProps {
  landing: LandingPage;
  open: boolean;
  onClose: () => void;
  onSaveDraft: (id: string, config: LandingPageConfig, fields: { name: string; slug: string }) => Promise<void>;
  onPublish: (landing: LandingPage, config: LandingPageConfig, fields: { name: string; slug: string }) => Promise<void>;
}

const parseJson = <T,>(value: string, label: string): T => {
  try {
    return JSON.parse(value) as T;
  } catch {
    throw new Error(`${label}: niepoprawny JSON`);
  }
};

const toPrettyJson = (value: unknown) => JSON.stringify(value ?? {}, null, 2);
const INVALID_PROPS_JSON_KEY = "invalidJsonDraft";

const INVALID_FAQ_ITEMS_JSON_KEY = "invalidFaqItemsJsonDraft";

const readString = (props: Record<string, unknown>, key: string) =>
  typeof props[key] === "string" ? (props[key] as string) : "";

const readBoolean = (props: Record<string, unknown>, key: string, fallback = false) =>
  typeof props[key] === "boolean" ? (props[key] as boolean) : fallback;

const serializeList = (value: unknown) => (Array.isArray(value) ? value.join("\n") : "");

const serializeMenuItems = (value: unknown) =>
  Array.isArray(value)
    ? value.map((item) => `${item.label ?? ""}|${item.href ?? ""}`).join("\n")
    : "Jak to działa?|#promo-video\nMetamorfozy|#transformations\nOpinie|#reviews\nFAQ|#faq";

const parseMenuItems = (value: string) =>
  value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [label, href] = line.split("|");
      return { label: label?.trim() ?? "", href: href?.trim() ?? "" };
    })
    .filter((item) => item.label);

interface SectionPropsEditorProps {
  section: LandingSectionConfig;
  onPropsChange: (props: Record<string, unknown>) => void;
}

const SectionPropsEditor = ({ section, onPropsChange }: SectionPropsEditorProps) => {
  const props = {
    ...getDefaultSectionProps(section.type),
    ...(section.props as Record<string, unknown>),
  };
  const updateProp = (key: string, value: unknown) => {
    const nextProps = { ...props, [key]: value };
    delete nextProps[INVALID_FAQ_ITEMS_JSON_KEY];
    onPropsChange(nextProps);
  };

  const simpleInput = (key: string, label: string, placeholder?: string) => (
    <Form.Item label={label}>
      <Input
        value={readString(props, key)}
        placeholder={placeholder}
        onChange={(event) => updateProp(key, event.target.value)}
      />
    </Form.Item>
  );

  const textArea = (key: string, label: string, rows = 3, placeholder?: string) => (
    <Form.Item label={label}>
      <Input.TextArea
        rows={rows}
        value={readString(props, key)}
        placeholder={placeholder}
        onChange={(event) => updateProp(key, event.target.value)}
      />
    </Form.Item>
  );

  switch (section.type) {
    case "headerV2":
      return (
        <div className="grid grid-cols-2 gap-4">
          {simpleInput("ctaLabel", "Tekst przycisku CTA")}
          {simpleInput("ctaClassName", "Klasy CSS przycisku")}
          <Form.Item label="Pokazuj CTA na mobile">
            <Switch
              checked={readBoolean(props, "showMobileCta", true)}
              onChange={(checked) => updateProp("showMobileCta", checked)}
            />
          </Form.Item>
        </div>
      );
    case "headerClassic":
      return (
        <>
          <div className="grid grid-cols-2 gap-4">
            {simpleInput("ctaLabel", "Tekst przycisku CTA")}
            {simpleInput("ctaClassName", "Klasy CSS przycisku")}
          </div>
          <Form.Item label="Menu, po jednej pozycji w linii: Label|#anchor">
            <Input.TextArea
              rows={5}
              value={serializeMenuItems(props.menuItems)}
              onChange={(event) => updateProp("menuItems", parseMenuItems(event.target.value))}
            />
          </Form.Item>
        </>
      );
    case "hollywoodHero":
      return (
        <>
          <div className="grid grid-cols-2 gap-4">
            {simpleInput("badge", "Badge")}
            {simpleInput("cta", "Tekst CTA")}
            {simpleInput("guarantee", "Tekst pod CTA")}
            {simpleInput("desktopVideoSrc", "Wideo desktop")}
            {simpleInput("mobileVideoSrc", "Wideo mobile")}
            {simpleInput("desktopPosterSrc", "Poster desktop")}
            {simpleInput("mobilePosterSrc", "Poster mobile")}
          </div>
          {textArea("headline", "Nagłówek", 4)}
        </>
      );
    case "metamorfozaHeroVideo":
      return (
        <div className="grid grid-cols-2 gap-4">
          {textArea("description", "Nagłówek / opis hero", 4)}
          {textArea("content", "Treść pod nagłówkiem", 4)}
        </div>
      );
    case "promoVideo":
      return (
        <>
          <div className="grid grid-cols-2 gap-4">
            {simpleInput("title", "Tytuł")}
            {simpleInput("videoId", "ID wideo")}
            {simpleInput("id", "Anchor / ID sekcji")}
            {simpleInput("className", "Klasy CSS sekcji")}
          </div>
          {textArea("description", "Opis", 3)}
          <Space>
            <span>Pokaż ozdobne okręgi</span>
            <Switch
              checked={readBoolean(props, "showCirlces")}
              onChange={(checked) => updateProp("showCirlces", checked)}
            />
            <span>Ukryj ikony</span>
            <Switch
              checked={readBoolean(props, "hideIcons")}
              onChange={(checked) => updateProp("hideIcons", checked)}
            />
          </Space>
        </>
      );
    case "formSection":
      return (
        <>
          <div className="grid grid-cols-2 gap-4">
            {simpleInput("ctaText", "Tekst CTA")}
            {simpleInput("benefitsTitle", "Tytuł listy benefitów")}
          </div>
          {textArea("heading", "Nagłówek", 3)}
          {textArea("description", "Opis", 3)}
          <Form.Item label="Benefity, każdy w osobnej linii">
            <Input.TextArea
              rows={4}
              value={serializeList(props.benefits)}
              onChange={(event) =>
                updateProp(
                  "benefits",
                  event.target.value
                    .split("\n")
                    .map((line) => line.trim())
                    .filter(Boolean),
                )
              }
            />
          </Form.Item>
        </>
      );
    case "quizBanner":
      return (
        <>
          <div className="grid grid-cols-2 gap-4">
            {simpleInput("cta", "Tekst CTA")}
            {simpleInput("className", "Klasy CSS sekcji")}
          </div>
          {textArea("heading", "Nagłówek", 3)}
          {textArea("description", "Opis", 3)}
        </>
      );
    case "metamorfozyCta":
      return (
        <>
          {textArea("heading", "Nagłówek", 4)}
          {simpleInput("cta", "Tekst CTA")}
        </>
      );
    case "hollywoodFaq":
    case "basicFaq":
      return (
        <>
          {textArea("heading", "Nagłówek", 3)}
          <Form.Item label="FAQ JSON">
            <Input.TextArea
              rows={8}
              status={INVALID_FAQ_ITEMS_JSON_KEY in props ? "error" : undefined}
              value={
                INVALID_FAQ_ITEMS_JSON_KEY in props
                  ? String(props[INVALID_FAQ_ITEMS_JSON_KEY])
                  : toPrettyJson(props.items ?? [])
              }
              onChange={(event) => {
                try {
                  const nextProps: Record<string, unknown> = {
                    ...props,
                    items: JSON.parse(event.target.value),
                  };
                  delete nextProps[INVALID_FAQ_ITEMS_JSON_KEY];
                  onPropsChange(nextProps);
                } catch {
                  onPropsChange({
                    ...props,
                    [INVALID_FAQ_ITEMS_JSON_KEY]: event.target.value,
                  });
                }
              }}
            />
          </Form.Item>
        </>
      );
    case "hollywoodFooter":
      return (
        <>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item label="Wariant">
              <Select
                value={readString(props, "variant") || "default"}
                options={[
                  { label: "Pełny", value: "default" },
                  { label: "Skrócony", value: "lean" },
                ]}
                onChange={(value) => updateProp("variant", value)}
              />
            </Form.Item>
            {simpleInput("cta", "Tekst CTA")}
            {simpleInput("metamorphosesCount", "Licznik metamorfoz")}
            {simpleInput("copyright", "Copyright")}
          </div>
          {textArea("heading", "Nagłówek", 3)}
          {textArea("description", "Opis", 3)}
        </>
      );
    case "pakietyStaticSection":
      return (
        <Form.Item label="Którą gotową sekcję Pakiety renderować">
          <Select
            value={readString(props, "variant") || "wystarczy"}
            options={pakietyStaticSectionOptions}
            onChange={(value) => updateProp("variant", value)}
          />
        </Form.Item>
      );
    case "pakietyCards":
      return (
        <Space>
          <span>Gradient</span>
          <Switch
            checked={readBoolean(props, "showGradient")}
            onChange={(checked) => updateProp("showGradient", checked)}
          />
          <span>Oferta downsell</span>
          <Switch
            checked={readBoolean(props, "showDownsellOffer")}
            onChange={(checked) => updateProp("showDownsellOffer", checked)}
          />
        </Space>
      );
    case "pakietyStatistics":
      return (
        <Form.Item label="Wariant statystyk">
          <Select
            value={readString(props, "kind") || "pakiety"}
            options={[
              { label: "Pakiety statistics", value: "pakiety" },
              { label: "Transformation statistics", value: "transformation" },
            ]}
            onChange={(value) => updateProp("kind", value)}
          />
        </Form.Item>
      );
    case "stickyPakietyCta":
      return (
        <div className="grid grid-cols-2 gap-4">
          {simpleInput("startSelector", "Selector startu")}
          {simpleInput("endSelector", "Selector końca")}
        </div>
      );
    case "reviews":
    case "transformationsGrid":
      return simpleInput("className", "Klasy CSS sekcji");
    default: {
      const definition = funnelSectionDefinitions[section.type];
      if (definition) {
        return <SchemaPropsEditor fields={definition.fields} props={props} onPropsChange={onPropsChange} />;
      }
      return (
        <Alert
          type="info"
          showIcon
          message="Ta sekcja nie ma jeszcze pól edytowalnych w CRM."
          description="Możesz zmienić jej kolejność, typ, widoczność i tracking. Pola contentowe dodajemy do sekcji, które faktycznie przyjmują propsy."
        />
      );
    }
  }
};

interface SortableSectionCardProps {
  section: LandingSectionConfig;
  onUpdate: (patch: Partial<LandingSectionConfig>) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  /** Funnel template sections are offered on funnel pages only (marketing landings don't load them). */
  isFunnelPage: boolean;
}

const SortableSectionCard = ({ section, onUpdate, onDuplicate, onDelete, isFunnelPage }: SortableSectionCardProps) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  });
  // section.id doubles as the React key and dnd-sortable id, so committing it on
  // every keystroke would remount the card (losing focus). Edit a local draft and
  // commit on blur instead.
  const [idDraft, setIdDraft] = useState(section.id);
  useEffect(() => setIdDraft(section.id), [section.id]);
  const commitId = () => {
    const nextId = idDraft.trim();
    if (nextId && nextId !== section.id) {
      onUpdate({ id: nextId });
    } else {
      setIdDraft(section.id);
    }
  };
  const advancedJsonValue =
    section.props && INVALID_PROPS_JSON_KEY in section.props
      ? String((section.props as Record<string, unknown>)[INVALID_PROPS_JSON_KEY])
      : toPrettyJson(section.props);

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
        position: "relative",
        zIndex: isDragging ? 10 : undefined,
      }}
    >
      <Card
        size="small"
        className="mb-3"
        title={
          <div className="flex flex-wrap items-center gap-3">
            <Button
              ref={setActivatorNodeRef}
              icon={<MenuOutlined />}
              {...attributes}
              {...listeners}
              aria-label="Przeciągnij sekcję"
            />
            <Switch checked={section.enabled} onChange={(checked) => onUpdate({ enabled: checked })} />
            <Input
              className="max-w-[220px]"
              value={idDraft}
              onChange={(event) => setIdDraft(event.target.value)}
              onBlur={commitId}
              onPressEnter={commitId}
            />
            <Select
              className="min-w-[260px]"
              value={section.type}
              options={
                isFunnelPage
                  ? landingSectionTypeOptions
                  : landingSectionTypeOptions.filter((option) => !funnelSectionDefinitions[option.value])
              }
              showSearch
              optionFilterProp="label"
              onChange={(value: LandingSectionType) => onUpdate({ type: value, props: getDefaultSectionProps(value) })}
            />
            <Input
              className="max-w-[220px]"
              value={section.trackingName}
              placeholder="trackingName"
              onChange={(event) => onUpdate({ trackingName: event.target.value })}
            />
          </div>
        }
        extra={
          <Space>
            <Button icon={<CopyOutlined />} onClick={onDuplicate} />
            <Button danger icon={<DeleteOutlined />} onClick={onDelete} />
          </Space>
        }
      >
        <SectionPropsEditor section={section} onPropsChange={(props) => onUpdate({ props })} />
        <Divider />
        <Collapse
          ghost
          items={[
            {
              key: "advanced",
              label: "Zaawansowane: JSON propsów",
              children: (
                <Input.TextArea
                  rows={8}
                  value={advancedJsonValue}
                  status={section.props && INVALID_PROPS_JSON_KEY in section.props ? "error" : undefined}
                  onChange={(event) => {
                    try {
                      onUpdate({ props: JSON.parse(event.target.value) });
                    } catch {
                      onUpdate({
                        props: { [INVALID_PROPS_JSON_KEY]: event.target.value },
                      });
                    }
                  }}
                />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

const LabeledField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: "#71717a" }}>{label}</div>
    {children}
  </div>
);

const NodeIdInput = ({ value, onCommit }: { value: string; onCommit: (value: string) => void }) => (
  <Input key={value} defaultValue={value} onBlur={(event) => onCommit(event.target.value)} />
);

const QuestionOptionsAdminEditor = ({
  options,
  onChange,
}: {
  options: LandingQuestionOption[];
  onChange: (options: LandingQuestionOption[]) => void;
}) => {
  const update = (index: number, patch: Partial<LandingQuestionOption>) =>
    onChange(options.map((option, i) => (i === index ? { ...option, ...patch } : option)));
  return (
    <Space direction="vertical" className="w-full">
      {options.map((option, index) => (
        <Card
          key={index}
          size="small"
          title={`Opcja ${index + 1}`}
          extra={
            <Button size="small" danger onClick={() => onChange(options.filter((_, i) => i !== index))}>
              Usuń
            </Button>
          }
        >
          <div className="grid grid-cols-3 gap-2">
            <Input value={option.id} onChange={(e) => update(index, { id: e.target.value })} placeholder="ID" />
            <Input
              value={option.label}
              onChange={(e) => update(index, { label: e.target.value })}
              placeholder="Etykieta"
            />
            <Input
              value={option.value}
              onChange={(e) => update(index, { value: e.target.value })}
              placeholder="Wartość"
            />
          </div>
        </Card>
      ))}
      <Button
        size="small"
        icon={<PlusOutlined />}
        onClick={() =>
          onChange([
            ...options,
            { id: `opcja-${options.length + 1}`, label: "Nowa odpowiedź", value: `opcja-${options.length + 1}` },
          ])
        }
      >
        Dodaj opcję
      </Button>
    </Space>
  );
};

const AdminNodeProps = ({
  node,
  onChange,
}: {
  node: SignupFlowNode;
  onChange: (props: SignupFlowNode["props"]) => void;
}) => {
  switch (node.type) {
    case "intro":
      return (
        <>
          <LabeledField label="Tytuł">
            <Input
              value={node.props.title ?? ""}
              onChange={(e) => onChange({ ...node.props, title: e.target.value })}
            />
          </LabeledField>
          <LabeledField label="Opis">
            <Input.TextArea
              rows={2}
              value={node.props.description ?? ""}
              onChange={(e) => onChange({ ...node.props, description: e.target.value })}
            />
          </LabeledField>
          <LabeledField label="Tekst przycisku">
            <Input
              value={node.props.ctaLabel ?? ""}
              onChange={(e) => onChange({ ...node.props, ctaLabel: e.target.value })}
            />
          </LabeledField>
        </>
      );
    case "question": {
      const question = node.props;
      return (
        <>
          <div className="grid grid-cols-2 gap-3">
            <LabeledField label="ID pytania (do warunków krawędzi)">
              <Input value={question.id} onChange={(e) => onChange({ ...question, id: e.target.value })} />
            </LabeledField>
            <LabeledField label="Tryb wyboru">
              <Select
                value={question.selectionMode ?? "single"}
                onChange={(value) => onChange({ ...question, selectionMode: value })}
                className="w-full"
                options={[
                  { value: "single", label: "Jedna odpowiedź" },
                  { value: "multi", label: "Wiele odpowiedzi" },
                ]}
              />
            </LabeledField>
          </div>
          <LabeledField label="Tytuł">
            <Input.TextArea
              rows={2}
              value={question.title}
              onChange={(e) => onChange({ ...question, title: e.target.value })}
            />
          </LabeledField>
          <LabeledField label="Auto-next po wyborze">
            <Switch
              checked={Boolean(question.autoAdvanceOnSelect)}
              onChange={(autoAdvanceOnSelect) => onChange({ ...question, autoAdvanceOnSelect })}
            />
          </LabeledField>
          <LabeledField label="Opcje">
            <QuestionOptionsAdminEditor
              options={question.options}
              onChange={(options) => onChange({ ...question, options })}
            />
          </LabeledField>
        </>
      );
    }
    case "userData":
      return (
        <>
          <LabeledField label="Tytuł">
            <Input
              value={node.props.title ?? ""}
              onChange={(e) => onChange({ ...node.props, title: e.target.value })}
            />
          </LabeledField>
          <LabeledField label="Opis">
            <Input.TextArea
              rows={2}
              value={node.props.description ?? ""}
              onChange={(e) => onChange({ ...node.props, description: e.target.value })}
            />
          </LabeledField>
          <div className="grid grid-cols-2 gap-3">
            <LabeledField label="Tekst przycisku">
              <Input
                value={node.props.ctaLabel ?? ""}
                onChange={(e) => onChange({ ...node.props, ctaLabel: e.target.value })}
              />
            </LabeledField>
            <LabeledField label="Pole telefonu">
              <Switch
                checked={Boolean(node.props.showPhone)}
                onChange={(showPhone) => onChange({ ...node.props, showPhone })}
              />
            </LabeledField>
          </div>
        </>
      );
    case "consultation":
      return (
        <>
          <LabeledField label="Tytuł">
            <Input
              value={node.props.title ?? ""}
              onChange={(e) => onChange({ ...node.props, title: e.target.value })}
            />
          </LabeledField>
          <div className="grid grid-cols-2 gap-3">
            <LabeledField label="Tekst przycisku">
              <Input
                value={node.props.ctaLabel ?? ""}
                onChange={(e) => onChange({ ...node.props, ctaLabel: e.target.value })}
              />
            </LabeledField>
            <LabeledField label="Ukryj szczegóły">
              <Switch
                checked={Boolean(node.props.hideDetails)}
                onChange={(hideDetails) => onChange({ ...node.props, hideDetails })}
              />
            </LabeledField>
          </div>
        </>
      );
    case "calendar":
      return (
        <LabeledField label="Calendly event URI (puste = z ustawień grafu)">
          <Input
            value={node.props.eventTypeUri ?? ""}
            onChange={(e) => onChange({ ...node.props, eventTypeUri: e.target.value })}
          />
        </LabeledField>
      );
    case "downsellChoice":
      return (
        <LabeledField label="Ukryj opcję specjalisty">
          <Switch
            checked={Boolean(node.props.hideSpecialistOption)}
            onChange={(hideSpecialistOption) => onChange({ ...node.props, hideSpecialistOption })}
          />
        </LabeledField>
      );
    case "downsell":
      return (
        <>
          <LabeledField label="Tytuł">
            <Input
              value={node.props.title ?? ""}
              onChange={(e) => onChange({ ...node.props, title: e.target.value })}
            />
          </LabeledField>
          <LabeledField label="Akapity (po jednym w linii)">
            <Input.TextArea
              rows={3}
              value={(node.props.paragraphs ?? []).join("\n")}
              onChange={(e) =>
                onChange({
                  ...node.props,
                  paragraphs: e.target.value
                    .split("\n")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />
          </LabeledField>
          <div className="grid grid-cols-2 gap-3">
            <LabeledField label="Tekst przycisku">
              <Input
                value={node.props.ctaLabel ?? ""}
                onChange={(e) => onChange({ ...node.props, ctaLabel: e.target.value })}
              />
            </LabeledField>
            <LabeledField label="URL wyzwania (bikini)">
              <Input
                value={node.props.bikiniChallengeUrl ?? ""}
                onChange={(e) => onChange({ ...node.props, bikiniChallengeUrl: e.target.value })}
              />
            </LabeledField>
          </div>
        </>
      );
    case "redirect":
      return (
        <>
          <LabeledField label="Akcja przekierowania">
            <Select
              value={node.props.action.type}
              onChange={(value: LandingSignupFlowActionType) =>
                onChange({
                  ...node.props,
                  action:
                    value === LandingSignupFlowActionType.GoToCheckoutVariant
                      ? { type: value, variantSlug: "" }
                      : { type: value },
                })
              }
              className="w-full"
              options={[
                { value: LandingSignupFlowActionType.GoToPackagesPage, label: "Przejdź na /pakiety" },
                { value: LandingSignupFlowActionType.GoToPackagesHash, label: "Przejdź do #packages" },
                { value: LandingSignupFlowActionType.GoToCheckoutVariant, label: "Przejdź do koszyka" },
                { value: LandingSignupFlowActionType.GoToBeforeConsultation, label: "Przejdź do przed-konsultacja" },
              ]}
            />
          </LabeledField>
          {node.props.action.type === LandingSignupFlowActionType.GoToCheckoutVariant ? (
            <LabeledField label="Slug koszyka (puste = domyślny)">
              <Input
                value={node.props.action.variantSlug ?? ""}
                onChange={(e) =>
                  onChange({
                    ...node.props,
                    action: { type: LandingSignupFlowActionType.GoToCheckoutVariant, variantSlug: e.target.value },
                  })
                }
              />
            </LabeledField>
          ) : null}
        </>
      );
    case "packages":
      return <Alert type="info" showIcon message="Konfigurację pakietów edytuj w trybie JSON (przełącznik u góry)." />;
    default:
      return null;
  }
};

const SortableGraphNodeAdminCard = ({
  node,
  onRename,
  onChangeType,
  onUpdateProps,
  onRemove,
}: {
  node: SignupFlowNode;
  onRename: (id: string) => void;
  onChangeType: (type: SignupFlowNodeType) => void;
  onUpdateProps: (props: SignupFlowNode["props"]) => void;
  onRemove: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: node.id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
    >
      <Card
        size="small"
        type="inner"
        title={
          <Space>
            <Button
              size="small"
              type="text"
              icon={<MenuOutlined />}
              aria-label="Przeciągnij krok"
              style={{ cursor: "grab" }}
              {...attributes}
              {...listeners}
            />
            <span>
              {signupFlowNodeTypeLabel(node.type)} · {node.id}
            </span>
          </Space>
        }
        extra={
          <Button size="small" danger onClick={onRemove}>
            Usuń
          </Button>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <LabeledField label="ID kroku">
            <NodeIdInput value={node.id} onCommit={onRename} />
          </LabeledField>
          <LabeledField label="Typ">
            <Select
              value={node.type}
              onChange={onChangeType}
              className="w-full"
              options={SIGNUP_FLOW_NODE_TYPES.map((type) => ({ value: type, label: signupFlowNodeTypeLabel(type) }))}
            />
          </LabeledField>
        </div>
        <AdminNodeProps node={node} onChange={onUpdateProps} />
      </Card>
    </div>
  );
};

const SignupFlowGraphAdminEditor = ({
  graph,
  onChange,
}: {
  graph: LandingSignupFlowGraph;
  onChange: (graph: LandingSignupFlowGraph) => void;
}) => {
  const [mode, setMode] = useState<"form" | "json">("form");
  const [jsonDraft, setJsonDraft] = useState(() => toPrettyJson(graph));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const nodeIds = graph.nodes.map((node) => node.id);
  const questionNodes = graph.nodes.filter(
    (node): node is Extract<SignupFlowNode, { type: "question" }> => node.type === "question",
  );
  const nodeOptions = nodeIds.map((id) => ({ value: id, label: id }));

  const renameNode = (index: number, nextId: string) => {
    const oldId = graph.nodes[index]!.id;
    const trimmed = nextId.trim();
    if (!trimmed || trimmed === oldId || graph.nodes.some((node, i) => i !== index && node.id === trimmed)) {
      return;
    }
    onChange({
      ...graph,
      entry: graph.entry === oldId ? trimmed : graph.entry,
      nodes: graph.nodes.map((node, i) => (i === index ? { ...node, id: trimmed } : node)),
      edges: graph.edges.map((edge) => ({
        ...edge,
        from: edge.from === oldId ? trimmed : edge.from,
        to: edge.to === oldId ? trimmed : edge.to,
      })),
    });
  };
  const changeNodeType = (index: number, type: SignupFlowNodeType) =>
    onChange({
      ...graph,
      nodes: graph.nodes.map((node, i) => (i === index ? createSignupFlowNode(type, node.id) : node)),
    });
  const updateNodeProps = (index: number, props: SignupFlowNode["props"]) =>
    onChange({
      ...graph,
      nodes: graph.nodes.map((node, i) => (i === index ? ({ ...node, props } as SignupFlowNode) : node)),
    });
  const removeNode = (index: number) => onChange({ ...graph, nodes: graph.nodes.filter((_, i) => i !== index) });
  const handleNodeDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = graph.nodes.findIndex((node) => node.id === active.id);
    const newIndex = graph.nodes.findIndex((node) => node.id === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    onChange({ ...graph, nodes: arrayMove(graph.nodes, oldIndex, newIndex) });
  };
  const addNode = (type: SignupFlowNodeType) => {
    let counter = graph.nodes.length + 1;
    let id = `${type}-${counter}`;
    while (nodeIds.includes(id)) {
      counter += 1;
      id = `${type}-${counter}`;
    }
    onChange({ ...graph, nodes: [...graph.nodes, createSignupFlowNode(type, id)] });
  };

  const updateEdge = (index: number, patch: Partial<SignupFlowEdge>) =>
    onChange({ ...graph, edges: graph.edges.map((edge, i) => (i === index ? { ...edge, ...patch } : edge)) });
  const removeEdge = (index: number) => onChange({ ...graph, edges: graph.edges.filter((_, i) => i !== index) });
  const addEdge = () =>
    onChange({ ...graph, edges: [...graph.edges, { from: nodeIds[0] ?? "", to: nodeIds[0] ?? "" }] });

  const applyJson = (value: string) => {
    setJsonDraft(value);
    try {
      const parsed = JSON.parse(value) as LandingSignupFlowGraph;
      setJsonError(null);
      onChange(parsed);
    } catch {
      setJsonError("Niepoprawny JSON");
    }
  };

  return (
    <Space direction="vertical" className="w-full" size="middle">
      <div className="flex items-center justify-between">
        <Typography.Text strong>Graf signup flow</Typography.Text>
        <Segmented
          value={mode}
          onChange={(value) => {
            if (value === "json") {
              setJsonDraft(toPrettyJson(graph));
              setJsonError(null);
            }
            setMode(value as "form" | "json");
          }}
          options={[
            { value: "form", label: "Formularz" },
            { value: "json", label: "JSON" },
          ]}
        />
      </div>

      {mode === "json" ? (
        <div>
          <Typography.Text type="secondary">Wklej lub skopiuj cały graf. Zmiany aplikują się na żywo.</Typography.Text>
          <Input.TextArea
            rows={24}
            value={jsonDraft}
            onChange={(event) => applyJson(event.target.value)}
            style={{ fontFamily: "monospace", marginTop: 8 }}
          />
          {jsonError ? <Typography.Text type="danger">{jsonError}</Typography.Text> : null}
        </div>
      ) : (
        <>
          <Card size="small" title="Ustawienia grafu">
            <div className="grid grid-cols-2 gap-3">
              <LabeledField label="Krok startowy">
                <Select
                  value={graph.entry}
                  onChange={(entry) => onChange({ ...graph, entry })}
                  className="w-full"
                  options={nodeOptions}
                />
              </LabeledField>
              <LabeledField label="Calendly event URI">
                <Input
                  value={graph.eventTypeUri}
                  onChange={(e) => onChange({ ...graph, eventTypeUri: e.target.value })}
                />
              </LabeledField>
              <LabeledField label="Kolor">
                <Select
                  value={graph.colorTheme ?? "gold"}
                  onChange={(colorTheme) => onChange({ ...graph, colorTheme })}
                  className="w-full"
                  options={[
                    { value: "gold", label: "Gold" },
                    { value: "pink", label: "Pink" },
                  ]}
                />
              </LabeledField>
              <LabeledField label="Flow">
                <Select
                  value={graph.flow ?? "checkout"}
                  onChange={(flow) => onChange({ ...graph, flow })}
                  className="w-full"
                  options={[
                    { value: "checkout", label: "Checkout" },
                    { value: "booking", label: "Booking" },
                  ]}
                />
              </LabeledField>
            </div>
          </Card>

          <Card size="small" title={`Kroki (${graph.nodes.length})`}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleNodeDragEnd}
            >
              <SortableContext items={nodeIds} strategy={verticalListSortingStrategy}>
                <Space direction="vertical" className="w-full">
                  {graph.nodes.map((node, index) => (
                    <SortableGraphNodeAdminCard
                      key={node.id}
                      node={node}
                      onRename={(value) => renameNode(index, value)}
                      onChangeType={(type) => changeNodeType(index, type)}
                      onUpdateProps={(props) => updateNodeProps(index, props)}
                      onRemove={() => removeNode(index)}
                    />
                  ))}
                </Space>
              </SortableContext>
            </DndContext>
            <div className="mt-3 flex flex-wrap gap-2">
              {SIGNUP_FLOW_NODE_TYPES.map((type) => (
                <Button key={type} size="small" icon={<PlusOutlined />} onClick={() => addNode(type)}>
                  {signupFlowNodeTypeLabel(type)}
                </Button>
              ))}
            </div>
          </Card>

          <Card size="small" title={`Przejścia (${graph.edges.length})`}>
            <Space direction="vertical" className="w-full">
              {graph.edges.map((edge, index) => (
                <Card
                  key={index}
                  size="small"
                  type="inner"
                  title={`Przejście ${index + 1}`}
                  extra={
                    <Button size="small" danger onClick={() => removeEdge(index)}>
                      Usuń
                    </Button>
                  }
                >
                  <div className="grid grid-cols-2 gap-3">
                    <LabeledField label="Z kroku">
                      <Select
                        value={edge.from}
                        onChange={(from) => updateEdge(index, { from })}
                        className="w-full"
                        options={nodeOptions}
                      />
                    </LabeledField>
                    <LabeledField label="Do kroku">
                      <Select
                        value={edge.to}
                        onChange={(to) => updateEdge(index, { to })}
                        className="w-full"
                        options={nodeOptions}
                      />
                    </LabeledField>
                  </div>
                  <LabeledField label="Wyjście (puste = domyślne; np. specialist, simplePlan, booked)">
                    <Input
                      value={edge.on ?? ""}
                      placeholder="domyślne"
                      onChange={(e) => updateEdge(index, { on: e.target.value || undefined })}
                    />
                  </LabeledField>
                  <div className="mb-2 flex items-center gap-2">
                    <Switch
                      checked={Boolean(edge.when)}
                      onChange={(checked) =>
                        updateEdge(index, {
                          when: checked ? { answerOf: questionNodes[0]?.props.id ?? "", equals: [] } : undefined,
                        })
                      }
                    />
                    <Typography.Text type="secondary">Warunek po odpowiedzi</Typography.Text>
                  </div>
                  {edge.when ? (
                    <div className="grid grid-cols-2 gap-3">
                      <LabeledField label="Pytanie">
                        <Select
                          value={edge.when.answerOf}
                          onChange={(answerOf) => updateEdge(index, { when: { ...edge.when!, answerOf } })}
                          className="w-full"
                          options={questionNodes.map((node) => ({ value: node.props.id, label: node.props.id }))}
                        />
                      </LabeledField>
                      <LabeledField label="Gdy odpowiedź = (po przecinku)">
                        <Input
                          value={edge.when.equals.join(", ")}
                          onChange={(e) =>
                            updateEdge(index, {
                              when: {
                                ...edge.when!,
                                equals: e.target.value
                                  .split(",")
                                  .map((item) => item.trim())
                                  .filter(Boolean),
                              },
                            })
                          }
                        />
                      </LabeledField>
                    </div>
                  ) : null}
                </Card>
              ))}
            </Space>
            <Button className="mt-3" size="small" icon={<PlusOutlined />} onClick={addEdge}>
              Dodaj przejście
            </Button>
          </Card>
        </>
      )}
    </Space>
  );
};

export const LandingEditor = ({ landing, open, onClose, onSaveDraft, onPublish }: LandingEditorProps) => {
  // Funnel step pages (TRE-2356) have no signup modal and render only on a funnel step.
  // A step template of any kind but a landing is a funnel page even if its slug lost the prefix.
  const isFunnelPage =
    (landing.kind ?? LandingPageKind.Landing) !== LandingPageKind.Landing ||
    landing.slug.startsWith(FUNNEL_TEMPLATE_SLUG_PREFIX);
  const [form] = Form.useForm<{
    name: string;
    slug: string;
    seo: string;
    tracking: string;
    layout: string;
    signupFlow: string;
  }>();
  const [sections, setSections] = useState<LandingSectionConfig[]>(landing.draftConfig.sections);
  const [graphV2, setGraphV2] = useState<LandingSignupFlowGraph | null>(landing.draftConfig.signupFlowV2 ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
  );

  const initialValues = useMemo(
    () => ({
      name: landing.name,
      slug: landing.slug,
      seo: toPrettyJson(landing.draftConfig.seo),
      tracking: toPrettyJson(landing.draftConfig.tracking),
      layout: toPrettyJson(landing.draftConfig.layout),
      signupFlow: toPrettyJson(landing.draftConfig.signupFlow),
    }),
    [landing],
  );

  const buildConfig = async () => {
    const values = await form.validateFields();
    const invalidSection = sections.find(
      (section) =>
        section.props && (INVALID_PROPS_JSON_KEY in section.props || INVALID_FAQ_ITEMS_JSON_KEY in section.props),
    );
    if (invalidSection) {
      throw new Error(`Sekcja ${invalidSection.id}: niepoprawny JSON propsów`);
    }

    const config: LandingPageConfig = {
      seo: parseJson(values.seo, "SEO"),
      tracking: parseJson(values.tracking, "Tracking"),
      layout: parseJson(values.layout, "Layout"),
      // Funnel pages hide the signup tabs (no field registered), so they keep their stored flow.
      signupFlow: isFunnelPage ? landing.draftConfig.signupFlow : parseJson(values.signupFlow, "Signup flow"),
      // v2 graph is optional — managed as state via the visual builder.
      ...(graphV2 ? { signupFlowV2: graphV2 } : {}),
      sections,
    };

    landingPageConfigSchema.parse(config);

    return {
      fields: {
        name: values.name,
        slug: isFunnelPage ? normalizeFunnelPageSlug(values.slug) : normalizeLandingSlug(values.slug),
      },
      config,
    };
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      const { config, fields } = await buildConfig();
      await onSaveDraft(landing.id, config, fields);
      message.success("Draft zapisany");
      onClose();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Nie udało się zapisać draftu");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    setIsSaving(true);
    try {
      const { config, fields } = await buildConfig();
      await onPublish(landing, config, fields);
      message.success("Landing opublikowany");
      onClose();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Nie udało się opublikować landingu");
    } finally {
      setIsSaving(false);
    }
  };

  const updateSection = (id: string, patch: Partial<LandingSectionConfig>) => {
    // Reject an id rename that would collide with another section — duplicate ids
    // corrupt the list (updateSection would then patch two cards at once and the
    // sortable renders duplicate keys).
    if (patch.id && patch.id !== id) {
      setSections((current) => {
        if (current.some((section) => section.id === patch.id)) {
          message.error(`Sekcja o ID "${patch.id}" już istnieje`);
          return current;
        }
        return current.map((section) => (section.id === id ? { ...section, ...patch } : section));
      });
      return;
    }

    setSections((current) => current.map((section) => (section.id === id ? { ...section, ...patch } : section)));
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    setSections((current) => {
      const oldIndex = current.findIndex((section) => section.id === active.id);
      const newIndex = current.findIndex((section) => section.id === over.id);
      if (oldIndex < 0 || newIndex < 0) {
        return current;
      }
      return arrayMove(current, oldIndex, newIndex);
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1100}
      title={`Landing: ${landing.name}`}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Zamknij
        </Button>,
        <Button key="save" icon={<SaveOutlined />} loading={isSaving} onClick={handleSaveDraft}>
          Save draft
        </Button>,
        <Button key="publish" type="primary" icon={<UploadOutlined />} loading={isSaving} onClick={handlePublish}>
          Publish
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" initialValues={initialValues}>
        <Tabs
          items={[
            {
              key: "general",
              label: "General",
              children: (
                <div className="grid grid-cols-2 gap-4">
                  <Form.Item name="name" label="Nazwa" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="slug" label="Slug">
                    <Input
                      addonBefore={isFunnelPage ? "/" : `/${LANDING_SLUG_PREFIX}`}
                      placeholder={isFunnelPage ? `${FUNNEL_TEMPLATE_SLUG_PREFIX}moj-szablon` : "test2026"}
                    />
                  </Form.Item>
                </div>
              ),
            },
            {
              key: "seo",
              label: "SEO",
              children: (
                <Form.Item name="seo" label="SEO JSON" rules={[{ required: true }]}>
                  <Input.TextArea rows={10} />
                </Form.Item>
              ),
            },
            {
              key: "tracking",
              label: "Tracking/Layout",
              children: (
                <div className="grid grid-cols-2 gap-4">
                  <Form.Item name="tracking" label="Tracking JSON" rules={[{ required: true }]}>
                    <Input.TextArea rows={10} />
                  </Form.Item>
                  <Form.Item name="layout" label="Layout JSON" rules={[{ required: true }]}>
                    <Input.TextArea rows={10} />
                  </Form.Item>
                </div>
              ),
            },
            ...(isFunnelPage
              ? []
              : [
                  {
                    key: "signup",
                    label: "Signup Flow",
                    children: (
                      <Form.Item name="signupFlow" label="Signup flow JSON" rules={[{ required: true }]}>
                        <Input.TextArea rows={18} />
                      </Form.Item>
                    ),
                  },
                  {
                    key: "signupV2",
                    label: "Signup Flow v2 (graf)",
                    children: (
                      <Space direction="vertical" className="w-full">
                        <Typography.Text type="secondary">
                          Grafowy flow — dowolna kolejność kroków i rozgałęzienia po odpowiedziach. Gdy włączony,
                          zastępuje Signup Flow v1 na tym landingu.
                        </Typography.Text>
                        {graphV2 ? (
                          <>
                            <Button danger onClick={() => setGraphV2(null)}>
                              Wyłącz graf (wróć do v1)
                            </Button>
                            <SignupFlowGraphAdminEditor graph={graphV2} onChange={setGraphV2} />
                          </>
                        ) : (
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() =>
                              setGraphV2(createStarterSignupFlowGraph(landing.draftConfig.signupFlow.eventTypeUri))
                            }
                          >
                            Włącz graf (szablon)
                          </Button>
                        )}
                      </Space>
                    ),
                  },
                ]),
            {
              key: "sections",
              label: "Sections",
              children: (
                <Space direction="vertical" className="w-full">
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() =>
                      setSections((current) => [
                        ...current,
                        {
                          id: `section-${crypto.randomUUID()}`,
                          type: "quizBanner",
                          enabled: true,
                          trackingName: "new_section",
                          props: getDefaultSectionProps("quizBanner"),
                        },
                      ])
                    }
                  >
                    Dodaj sekcję
                  </Button>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={sections.map((section) => section.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {sections.map((section) => (
                        <SortableSectionCard
                          key={section.id}
                          section={section}
                          isFunnelPage={isFunnelPage}
                          onUpdate={(patch) => updateSection(section.id, patch)}
                          onDuplicate={() =>
                            setSections((current) => [
                              ...current,
                              {
                                ...section,
                                id: `${section.id}-copy-${crypto.randomUUID().slice(0, 6)}`,
                              },
                            ])
                          }
                          onDelete={() => setSections((current) => current.filter((item) => item.id !== section.id))}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </Space>
              ),
            },
          ]}
        />
      </Form>
    </Modal>
  );
};
